import { useEffect, useState } from "react";
import { withRouter, useParams, RouteComponentProps } from "react-router";
import {
  IonButton,
  IonContent,
  IonItem,
  IonLabel,
  IonNote,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  useIonToast,
} from "@ionic/react";

import Header from "src/components/Header";
import PanelFiscalizacion from "src/components/Fiscalizacion/PanelFiscalizacion";
import "./Fiscalizacion.css";
import {
  ILimpiezaGeneral,
  fetchLimpiezaGeneral,
  postLimpiezaGeneral,
} from "src/services/Fiscalizacion/Fiscalizacion";
import { useFiscalizacion } from "src/components/Fiscalizacion/FiscalizacionContext";

interface Params {
  fis_id: string;
  nombre: string;
  codigo: string;
  objetivo: string;
  ps_id: string;
  cod_id: string;
}

const CALIFICACIONES = [
  "Excelente",
  "Muy bueno",
  "Bueno",
  "Regular",
  "Deficiente",
];

// Calificaciones que exigen cargar acciones correctivas
const CALIFICACIONES_ACCIONES_CORRECTIVAS = ["bueno", "regular", "deficiente"];

const normalizarTexto = (valor: string) =>
  String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const requiereAccionesCorrectivas = (calificacion: string) =>
  CALIFICACIONES_ACCIONES_CORRECTIVAS.includes(normalizarTexto(calificacion));

const FiscalizarLimpieza: React.FC<RouteComponentProps> = ({ history }) => {
  const { setInformacionFiscalizacionLP } = useFiscalizacion();
  const { fis_id, objetivo } = useParams<Params>();
  const [present] = useIonToast();

  const [form, setForm] = useState<ILimpiezaGeneral>({
    calificacion: "",
    comentario: "",
    acciones_correctivas: "",
  });
  const [comentarioRequerido, setComentarioRequerido] = useState(
    normalizarTexto(objetivo) !== "reunion con el cliente"
  );
  const accionesRequeridas = requiereAccionesCorrectivas(form.calificacion);

  useEffect(() => {
    const cargarLimpiezaGeneral = async () => {
      const resultado = await fetchLimpiezaGeneral(Number(fis_id));
      if (resultado.error) {
        present(resultado.error, 4000);
      }

      setForm({
        calificacion: resultado.datos.calificacion || "",
        comentario: resultado.datos.comentario || "",
        acciones_correctivas: resultado.datos.acciones_correctivas || "",
        id: resultado.datos.id,
        fecha: resultado.datos.fecha,
      });
      setComentarioRequerido(resultado.comentario_requerido);
    };

    cargarLimpiezaGeneral();
  }, [fis_id, present]);

  const guardar = async () => {
    if (!form.calificacion) {
      present("Seleccioná una calificación antes de guardar.", 4000);
      return;
    }

    if (comentarioRequerido && !form.comentario.trim()) {
      present("El comentario es obligatorio para este objetivo.", 4000);
      return;
    }

    if (accionesRequeridas && !form.acciones_correctivas.trim()) {
      present(
        "Las acciones correctivas son obligatorias para esta calificación.",
        4000
      );
      return;
    }

    const datos: ILimpiezaGeneral = {
      ...form,
      acciones_correctivas: accionesRequeridas
        ? form.acciones_correctivas
        : "",
    };

    const resultado = await postLimpiezaGeneral(Number(fis_id), datos);
    if (!resultado.exito) {
      present(resultado.info, 4000);
      return;
    }

    setInformacionFiscalizacionLP({
      calificacion: datos.calificacion,
      comentario: datos.comentario,
      acciones_correctivas: datos.acciones_correctivas,
      id: datos.id,
      fecha: datos.fecha,
    });
    present(resultado.info, 3000);
    history.goBack();
  };

  return (
    <IonPage>
      <Header titulo="Fiscalización - Limpieza General" />

      <IonContent id="fiscalizacion">
        <div className="fisContainer">
          <PanelFiscalizacion />

          <div className="fisCard">
            <div className="fisCard_title--area">
              <img src="assets/icon/fisc-COD4.svg" alt="Limpieza General" />
              <h1>Nivel de Limpieza General</h1>
            </div>

            <IonItem className="fisField fisFieldSelect">
              <IonLabel position="stacked">Calificación</IonLabel>
              <IonSelect
                className="fisFieldSelectControl"
                interface="action-sheet"
                placeholder="Seleccioná una calificación"
                value={form.calificacion}
                onIonChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    calificacion: String(e.detail.value || ""),
                  }))
                }
              >
                {CALIFICACIONES.map((opcion) => (
                  <IonSelectOption key={opcion} value={opcion}>
                    {opcion}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">
                Comentario {comentarioRequerido ? "(Obligatorio)" : "(Opcional)"}
              </IonLabel>
              <IonTextarea
                autoGrow={true}
                placeholder="Ingresá un comentario sobre el nivel de limpieza general"
                value={form.comentario}
                onIonChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    comentario: String(e.detail.value || ""),
                  }))
                }
              />
            </IonItem>

            {accionesRequeridas && (
              <>
                <IonItem>
                  <IonLabel position="stacked">
                    Acciones Correctivas (Obligatorio)
                  </IonLabel>
                  <IonTextarea
                    autoGrow={true}
                    placeholder="Ingresá las acciones correctivas a implementar"
                    value={form.acciones_correctivas}
                    onIonChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        acciones_correctivas: String(e.detail.value || ""),
                      }))
                    }
                  />
                </IonItem>

                <IonNote color="medium">
                  Para las calificaciones Bueno, Regular y Deficiente debés
                  cargar las acciones correctivas.
                </IonNote>
              </>
            )}

            <IonNote color="medium">
              {comentarioRequerido
                ? "Debés dejar un comentario para este objetivo de fiscalización."
                : "Para el objetivo Reunión con el Cliente, el comentario no es obligatorio."}
            </IonNote>

            <IonButton onClick={guardar}>Guardar</IonButton>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default withRouter(FiscalizarLimpieza);
